import type { SetupIntent } from '@stripe/stripe-js';

import type { CreatePartnerSetupElementResponse } from '@ctct/infrastructure-layer';
import { stripeApi } from '@ctct/infrastructure-layer';
import type { SingleBillPaymentMethodsPageStore } from '@ctct/state-client-layer';

import type { SingleBillStripeBillingInfo } from './DataPreparationService';
import { singleBillPaymentMethodsEventBus } from '../SingleBillPaymentMethodsEventBus';

interface StripeSubmissionDetails {
  cardType?: string;
  cardLast4Digits?: string;
  cardExpirationDate?: string;
}

export class SingleBillStripeElementsService {
  private baselineSavedPaymentMethodToken: string | null;

  private hasProcessedInitialStripeSelection = false;

  constructor(
    private setupElementResponse: CreatePartnerSetupElementResponse | null,
    private pageStore: SingleBillPaymentMethodsPageStore,
    currentCreditCardToken: string | null,
    private isStripePayments: boolean
  ) {
    this.baselineSavedPaymentMethodToken = currentCreditCardToken;
  }

  public data() {
    return {
      shouldUseStripeElement: () =>
        this.isStripePayments ||
        Boolean(this.setupElementResponse?.clientSecret && this.setupElementResponse?.customerSessionClientSecret),
      getSetupIntentId: () => this.setupElementResponse?.setupIntentId ?? null,
    };
  }

  public handlers() {
    return {
      onMountStripeElementDiv: async () => {
        if (!this.setupElementResponse) {
          return;
        }

        const stripeElementFacade = await stripeApi.getStripeElementsFacade();

        stripeElementFacade.destroy();

        const selectedBillingCountry = this.pageStore.getState().selectedBillingCountry ?? undefined;

        await stripeElementFacade.createAndMountPaymentElement(
          {
            clientSecret: this.setupElementResponse.clientSecret,
            customerSessionClientSecret: this.setupElementResponse.customerSessionClientSecret,
            customPaymentMethods: [],
            appearance: stripeApi.getDefaultStripeAppearance(),
          },
          {
            layout: 'tabs',
            defaultValues: {
              billingDetails: {
                address: {
                  country: selectedBillingCountry ?? undefined,
                },
              },
            },
            terms: {
              card: 'never',
              usBankAccount: 'never',
            },
            fields: {
              billingDetails: {
                name: 'never',
                email: 'never',
                phone: 'never',
                address: {
                  country: 'never',
                  postalCode: 'never',
                },
              },
            },
          }
        );

        stripeElementFacade.addChangeListener((event) => {
          const selectedMethod = event.paymentMethodType;
          const selectedPaymentMethodId = event.paymentMethodId;

          const isSavedLikeSelection =
            selectedMethod === 'saved' || selectedMethod === 'card' || selectedMethod === 'us_bank_account';

          // Stripe can emit an initial selection event on mount based on customer session data.
          // Treat that as the baseline persisted state, not a user edit.
          if (!this.hasProcessedInitialStripeSelection && isSavedLikeSelection && selectedPaymentMethodId) {
            this.hasProcessedInitialStripeSelection = true;
            this.baselineSavedPaymentMethodToken = selectedPaymentMethodId;
            this.pageStore.setState({ paymentMethodCanBeUpdated: false });
            singleBillPaymentMethodsEventBus.emit('singleBillPaymentMethodCanBeUpdated', false, { source: 'onMount' });

            return;
          }

          if (!this.baselineSavedPaymentMethodToken && selectedPaymentMethodId) {
            this.baselineSavedPaymentMethodToken = selectedPaymentMethodId;
          }

          const isSavedSelectionByType = selectedMethod === 'saved';
          const isSelectingBaselineSavedMethod =
            Boolean(selectedPaymentMethodId) && selectedPaymentMethodId === this.baselineSavedPaymentMethodToken;
          const isExistingSavedCardSelection = selectedMethod === 'card' && isSelectingBaselineSavedMethod;
          const isExistingSavedBankAccountSelection =
            selectedMethod === 'us_bank_account' && isSelectingBaselineSavedMethod;
          const isCardTabWithEmptyInputs =
            selectedMethod === 'card' && !selectedPaymentMethodId && event.empty === true;

          const isSelectingDifferentSavedMethod =
            Boolean(selectedPaymentMethodId) && selectedPaymentMethodId !== this.baselineSavedPaymentMethodToken;
          const isEnteringNewCardDetails = selectedMethod === 'card' && event.complete && !selectedPaymentMethodId;
          const isEnteringNewBankAccountDetails =
            selectedMethod === 'us_bank_account' && event.complete && !selectedPaymentMethodId;
          const hasRealPaymentMethodChange =
            isSelectingDifferentSavedMethod || isEnteringNewCardDetails || isEnteringNewBankAccountDetails;

          if (hasRealPaymentMethodChange) {
            this.pageStore.setState({ hasInteractedWithPaymentElement: true });
          }

          const canBeUpdated =
            !isSavedSelectionByType &&
            !isExistingSavedCardSelection &&
            !isExistingSavedBankAccountSelection &&
            !isCardTabWithEmptyInputs &&
            hasRealPaymentMethodChange;

          this.pageStore.setState({ paymentMethodCanBeUpdated: canBeUpdated });
          singleBillPaymentMethodsEventBus.emit('singleBillPaymentMethodCanBeUpdated', canBeUpdated, {
            source: 'user',
          });
        });
      },

      reportPersistedStripePaymentMethod: (paymentMethodId: string) => {
        this.baselineSavedPaymentMethodToken = paymentMethodId;
        this.hasProcessedInitialStripeSelection = true;
      },

      submitPaymentMethod: async (billingInfo: SingleBillStripeBillingInfo) => {
        const stripeElementFacade = await stripeApi.getStripeElementsFacade();

        const { error, paymentMethodId } = await stripeElementFacade.submit({
          returnUrl: stripeApi.getReturnUrl(),
          postalCode: billingInfo.postalCode,
          countryCode: billingInfo.countryCode,
          fullName: billingInfo.fullName,
          email: billingInfo.email,
          phone: billingInfo.phone,
        });

        if (error) {
          throw new Error(error.message);
        }

        if (!paymentMethodId) {
          throw new Error('Could not create payment method');
        }

        const selectedPaymentMethod = await stripeElementFacade.getSelectedPaymentMethod();
        const paymentMethodDetails = await this.getSubmissionDetails(paymentMethodId);

        return {
          paymentMethodId,
          paymentMethodType: selectedPaymentMethod,
          ...paymentMethodDetails,
        };
      },
    };
  }

  private async getSubmissionDetails(paymentMethodId: string): Promise<StripeSubmissionDetails> {
    if (!this.setupElementResponse?.clientSecret) {
      return {};
    }

    try {
      const setupIntent = await stripeApi.retrieveSetupIntent(this.setupElementResponse.clientSecret);

      if (!setupIntent?.payment_method || typeof setupIntent.payment_method === 'string') {
        return {};
      }

      return this.extractPaymentMethodDetails(setupIntent, paymentMethodId);
    } catch (error) {
      console.warn('SingleBillStripeElementsService: failed to enrich submission details', error);

      return {};
    }
  }

  private extractPaymentMethodDetails(setupIntent: SetupIntent, paymentMethodId: string): StripeSubmissionDetails {
    const paymentMethod = setupIntent.payment_method;

    if (!paymentMethod || typeof paymentMethod === 'string' || paymentMethod.id !== paymentMethodId) {
      return {};
    }

    const cardType = paymentMethod.card?.brand;
    const cardLast4Digits = paymentMethod.card?.last4 ?? paymentMethod.us_bank_account?.last4 ?? undefined;
    const cardExpirationDate =
      paymentMethod.card?.exp_month && paymentMethod.card?.exp_year
        ? `${String(paymentMethod.card.exp_month).padStart(2, '0')}/${paymentMethod.card.exp_year}`
        : undefined;

    return {
      cardType,
      cardLast4Digits,
      cardExpirationDate,
    };
  }
}
